import { useState, useRef, useEffect, useCallback } from "react";
import {
  TEAMS,
  LEAGUES,
  PERFECT_OUTS,
  TWO_MIN_SECONDS,
  INPUT_LOCK_MS,
  FEEDBACK_MS,
  buildRound,
  roundLeague,
} from "./protocolEngine";

// ---------------------------------------------------------------------------
// The Protocol — logo × name interference game.
//
// Faithful 1:1 React port of the original single-file build. The round
// generator lives in ./protocolEngine (lifted verbatim, invariant-tested).
// This component owns only the plumbing: screen flow, click handling, the
// clock, and the results/share screens. All CSS is scoped under `.protocol`
// so nothing leaks into the rest of the app.
//
// Asset path: logos + league PNGs + title/mode art are served from /assets/
// (i.e. public/assets/). Text placeholders render via an onerror fallback,
// so the game is fully playable before the logo files land.
// ---------------------------------------------------------------------------

const ASSET_DIR = "/assets/";

const styles = `@font-face { font-family: 'Anton'; font-style: normal; font-weight: 400; font-display: swap; src: url('/anton-v27-latin-regular.woff2') format('woff2'); }
@font-face { font-family: 'Oswald'; font-style: normal; font-weight: 600; font-display: swap; src: url('/oswald-v57-latin-600.woff2') format('woff2'); }
@font-face { font-family: 'Barlow Condensed'; font-style: normal; font-weight: 500; font-display: swap; src: url('/fonts/BarlowCondensed-SemiBold.woff2') format('woff2'); }
@font-face { font-family: 'Barlow Condensed'; font-style: normal; font-weight: 600; font-display: swap; src: url('/fonts/BarlowCondensed-SemiBold.woff2') format('woff2'); }
@font-face { font-family: 'Barlow Condensed'; font-style: normal; font-weight: 700; font-display: swap; src: url('/fonts/BarlowCondensed-Bold.woff2') format('woff2'); }

.protocol{
  --ink:#000000;         /* pure black base */
  --paper:#0a0a0a;
  --white:#f6f5f1;
  --dim:#8b8b86;
  --blue:#1a2ec7;        /* comic electric blue */
  --blue2:#3346ff;
  --red:#e2211c;         /* comic red */
  --red2:#ff3b30;
  --yellow:#ffd21a;      /* comic yellow */
  --green:#22c55e;       /* correct */
  --line:#242424;
  --panel:#111;
  --pick:#ffd21a;        /* selection = yellow */
}
.protocol *{box-sizing:border-box;-webkit-tap-highlight-color:transparent}
.protocol{margin:0;padding:0;background:var(--ink)}
.protocol{
  font-family:'Barlow Condensed',system-ui,sans-serif;
  color:var(--white);
  min-height:100dvh;
  display:flex;flex-direction:column;align-items:center;
  overflow-x:hidden;
  /* faint halftone dot texture over pure black */
  background-image:radial-gradient(rgba(255,255,255,.028) 1px,transparent 1.4px);
  background-size:7px 7px;
}
.protocol #app{width:100%;max-width:460px;padding:14px 15px 40px;flex:1;display:flex;flex-direction:column}
.protocol /* comic outline text helper */
.outline{
  -webkit-text-stroke:2.5px #000;
  paint-order:stroke fill;
  text-shadow:3px 3px 0 #000;
}
.protocol /* screens */
.screen{display:none;flex:1;flex-direction:column;animation:pr-pop .32s cubic-bezier(.2,1.1,.35,1)}
.protocol .screen.on{display:flex}
@keyframes pr-pop{from{opacity:0;transform:scale(.97) translateY(8px)}to{opacity:1;transform:none}}
.protocol /* ---------- HOME ---------- */
.title-art{width:100%;max-width:340px;margin:8px auto 0;display:block;filter:drop-shadow(0 6px 18px rgba(26,46,199,.35))}
.protocol .subtitle{font-family:'Oswald';font-weight:600;font-size:11px;letter-spacing:.24em;text-transform:uppercase;
  color:var(--dim);text-align:center;margin:2px 0 20px}
.protocol .rulebox{border:2px solid var(--line);border-radius:4px;background:var(--panel);
  padding:14px 15px;margin:0 0 8px;position:relative}
.protocol .rulebox::before{content:"THE RULE";position:absolute;top:-9px;left:12px;background:var(--yellow);color:#000;
  font-family:'Oswald';font-weight:700;font-size:10px;letter-spacing:.14em;padding:2px 8px;border-radius:2px}
.protocol .rulebox p{margin:4px 0 0;font-size:15.5px;line-height:1.45;color:var(--white)}
.protocol .rulebox b{color:var(--yellow)}
.protocol .rulebox .ex{margin-top:10px;padding-top:10px;border-top:1px dashed var(--line);
  font-size:13px;color:var(--dim);line-height:1.4}
.protocol .rulebox .ex .sw{color:var(--red2);font-weight:700}
.protocol .pick-label{font-family:'Oswald';font-weight:700;font-size:11px;letter-spacing:.2em;text-transform:uppercase;
  color:var(--dim);margin:22px 2px 11px;display:flex;align-items:center;gap:9px}
.protocol .pick-label::after{content:"";flex:1;height:2px;background:var(--line)}
.protocol /* mode cards: rounded-square art (uncropped) + text rectangle */
.modes{display:flex;flex-direction:column;gap:14px}
.protocol .mode{border:3px solid #000;border-radius:8px;cursor:pointer;position:relative;
  display:flex;align-items:stretch;gap:0;padding:0;overflow:hidden;text-align:left;
  background:var(--panel);box-shadow:4px 4px 0 var(--blue);transition:transform .1s,box-shadow .1s}
.protocol .mode:active{transform:translate(2px,2px);box-shadow:2px 2px 0 var(--blue)}
.protocol .mode.two{box-shadow:4px 4px 0 var(--red)}
.protocol .mode.two:active{box-shadow:2px 2px 0 var(--red)}
.protocol .mode .art{flex:0 0 116px;width:116px;height:116px;background:#000;border-right:3px solid #000;
  display:flex;align-items:center;justify-content:center;overflow:hidden}
.protocol .mode .art img{width:100%;height:100%;object-fit:contain}
.protocol /* CONTAIN = no crop, .protocol full logo visible */
.mode .cap{flex:1;display:flex;flex-direction:column;justify-content:center;padding:12px 14px;position:relative}
.protocol .mode .cap .nm{font-family:'Anton';font-size:27px;line-height:.9;text-transform:uppercase;letter-spacing:.01em;
  color:var(--white);-webkit-text-stroke:.5px #000;paint-order:stroke fill}
.protocol .mode.perfect .cap .nm{color:var(--white)}
.protocol .mode .cap .tag{display:inline-block;align-self:flex-start;margin:7px 0;font-family:'Oswald';font-weight:700;font-size:10px;
  letter-spacing:.1em;text-transform:uppercase;color:#000;background:var(--yellow);padding:3px 8px;border-radius:2px;border:1.5px solid #000}
.protocol .mode.two .cap .tag{background:var(--red2);color:#fff}
.protocol .mode .cap .md{font-family:'Barlow Condensed';font-weight:600;font-size:12.5px;color:var(--dim);line-height:1.25}
.protocol /* difficulty toggle */
.diff{display:flex;gap:10px;margin-top:4px}
.protocol .diff .d{flex:1;border:2.5px solid var(--line);border-radius:5px;background:var(--panel);cursor:pointer;
  padding:11px 8px;text-align:center;transition:border-color .14s,background .14s,transform .08s}
.protocol .diff .d:active{transform:scale(.97)}
.protocol .diff .d.sel{border-color:var(--yellow);background:rgba(255,210,26,.1)}
.protocol .diff .d .dt{font-family:'Anton';font-size:19px;letter-spacing:.02em;text-transform:uppercase;line-height:1;color:var(--white)}
.protocol .diff .d.sel .dt{color:var(--yellow)}
.protocol .diff .d .dd{font-family:'Oswald';font-weight:500;font-size:9.5px;letter-spacing:.08em;text-transform:uppercase;
  color:var(--dim);margin-top:4px}
.protocol /* league logo chips */
.lg .logo-chip{width:100%;height:44px;display:flex;align-items:center;justify-content:center;
  background:#fff;border-radius:4px;margin-bottom:5px;overflow:hidden}
.protocol .lg .logo-chip img{max-width:82%;max-height:82%;object-fit:contain}
.protocol .lg.sink .logo-chip{background:linear-gradient(90deg,#1a2ec7,#e2211c);gap:3px;padding:0 4px}
.protocol .lg.sink .logo-chip img{max-height:64%;background:#fff;border-radius:3px;padding:1px}
.protocol /* leagues */
.leagues{display:grid;grid-template-columns:repeat(3,1fr);gap:10px}
.protocol .lg{border:2.5px solid var(--line);border-radius:5px;padding:15px 6px;text-align:center;cursor:pointer;
  background:var(--panel);transition:border-color .14s,background .14s,transform .08s}
.protocol .lg:active{transform:scale(.96)}
.protocol .lg.sel{border-color:var(--yellow);background:rgba(255,210,26,.1)}
.protocol .lg .ab{font-family:'Anton';font-size:22px;letter-spacing:.02em}
.protocol .lg.sel .ab{color:var(--yellow)}
.protocol .lg .ct{font-family:'Oswald';font-weight:500;font-size:9px;letter-spacing:.08em;color:var(--dim);text-transform:uppercase;margin-top:3px}
.protocol .lg.sink{grid-column:1/-1;background:linear-gradient(90deg,rgba(26,46,199,.18),rgba(226,33,28,.18))}
.protocol .lg.sink .ab{font-size:19px}
.protocol .btn{display:block;width:100%;border:3px solid #000;border-radius:6px;padding:15px;margin-top:20px;cursor:pointer;
  font-family:'Anton';font-size:22px;letter-spacing:.04em;text-transform:uppercase;
  background:var(--yellow);color:#000;box-shadow:4px 4px 0 #000;transition:transform .1s,box-shadow .1s}
.protocol .btn:disabled{opacity:.35;cursor:not-allowed;box-shadow:none}
.protocol .btn:not(:disabled):active{transform:translate(2px,2px);box-shadow:2px 2px 0 #000}
.protocol .btn.ghost{background:transparent;color:var(--dim);border:2px solid var(--line);box-shadow:none;font-size:16px}
.protocol .btn.ghost:active{transform:none}
.protocol /* ---------- HUD ---------- */
.hud{display:flex;align-items:center;justify-content:space-between;margin-bottom:6px;min-height:40px}
.protocol .hud .chip{font-family:'Anton';font-size:15px;letter-spacing:.03em;text-transform:uppercase;color:var(--white);
  border:2px solid var(--line);border-radius:3px;padding:3px 9px;background:var(--panel)}
.protocol .hud .chip b{color:var(--yellow)}
.protocol .clock{font-family:'Anton';font-size:34px;letter-spacing:.01em;color:var(--red2);font-variant-numeric:tabular-nums;line-height:1;
  -webkit-text-stroke:1.5px #000;paint-order:stroke fill}
.protocol .clock.count{color:var(--yellow)}
.protocol .clock.paused{animation:pr-blink 1s steps(2) infinite}
@keyframes pr-blink{50%{opacity:.3}}
.protocol .prog{font-family:'Oswald';font-weight:600;font-size:11px;letter-spacing:.1em;text-transform:uppercase;color:var(--dim);text-align:right;min-width:52px}
.protocol .prog b{color:var(--white);font-size:16px}
.protocol /* out/count strip */
.strip{display:flex;align-items:center;gap:4px;margin:2px 0 2px;flex-wrap:wrap;min-height:16px}
.protocol .pip{width:10px;height:10px;border-radius:50%;border:1.5px solid var(--line);transition:all .22s}
.protocol .pip.good{background:var(--green);border-color:var(--green);box-shadow:0 0 6px rgba(34,197,94,.55)}
.protocol .pip.bad{background:var(--red2);border-color:var(--red2)}
.protocol .strip .lbl{font-family:'Oswald';font-weight:700;font-size:9px;letter-spacing:.12em;text-transform:uppercase;color:var(--dim);margin-right:4px}
.protocol .prompt{font-family:'Barlow Condensed';font-weight:600;font-size:14px;letter-spacing:.02em;color:var(--dim);
  text-align:center;margin:10px 0 12px}
.protocol .prompt b{color:var(--white)}
.protocol /* ---------- CARD GRID ---------- */
.grid{display:grid;gap:11px;flex:1;align-content:center}
.protocol .grid.g4{grid-template-columns:1fr 1fr}
.protocol .grid.g6{grid-template-columns:1fr 1fr}
.protocol .card{border:3px solid #000;border-radius:6px;background:var(--panel);
  display:flex;flex-direction:column;align-items:center;justify-content:center;gap:9px;
  padding:14px 8px;cursor:pointer;position:relative;overflow:hidden;
  box-shadow:3px 3px 0 var(--blue);transition:transform .08s,box-shadow .08s,border-color .12s}
.protocol .grid.g6 .card{padding:9px 6px;gap:6px}
.protocol .card:active{transform:translate(1.5px,1.5px);box-shadow:1.5px 1.5px 0 var(--blue)}
.protocol .card .lw{width:66px;height:66px;display:flex;align-items:center;justify-content:center}
.protocol .grid.g6 .card .lw{width:50px;height:50px}
.protocol .card .lw img{max-width:100%;max-height:100%;object-fit:contain;filter:drop-shadow(0 2px 5px rgba(0,0,0,.5))}
.protocol .card .noimg{width:62px;height:62px;border-radius:6px;border:1.5px dashed var(--line);display:flex;align-items:center;
  justify-content:center;text-align:center;font-family:'Barlow Condensed';font-weight:700;font-size:10px;color:var(--dim);padding:4px;line-height:1.05}
.protocol .grid.g6 .card .noimg{width:48px;height:48px;font-size:8.5px}
.protocol .card .label{font-family:'Anton';font-size:25px;text-transform:uppercase;letter-spacing:.005em;line-height:.92;text-align:center;
  -webkit-text-stroke:.5px #000;paint-order:stroke fill}
.protocol .grid.g6 .card .label{font-size:19px}
.protocol .card .ord{position:absolute;top:6px;left:8px;font-family:'Oswald';font-weight:700;font-size:10px;color:var(--dim);opacity:.6}
.protocol .card.sel{border-color:var(--pick);box-shadow:3px 3px 0 var(--yellow)}
.protocol .card.sel .badge-n{content:"";position:absolute;top:6px;right:8px;width:20px;height:20px;border-radius:50%;background:var(--yellow);
  color:#000;font-family:'Anton';font-size:12px;display:flex;align-items:center;justify-content:center;border:1.5px solid #000}
.protocol .card.correct{border-color:var(--green);box-shadow:3px 3px 0 var(--green)}
.protocol .card.miss{border-color:var(--red2);box-shadow:3px 3px 0 var(--red2)}
.protocol .card.dim{opacity:.32}
.protocol .mk{position:absolute;top:6px;right:8px;width:21px;height:21px;border-radius:50%;display:none;
  align-items:center;justify-content:center;font-family:'Anton';font-size:12px;border:1.5px solid #000;z-index:3}
.protocol .card.sel .mk{display:flex;background:var(--yellow);color:#000}
.protocol .card.correct .mk{display:flex;background:var(--green);color:#000}
.protocol .card.miss .mk{display:flex;background:var(--red2);color:#fff}
.protocol .grid.locked .card{pointer-events:none}
.protocol /* timeout */
.toBar{margin-top:11px}
.protocol .toBtn{width:100%;border:2.5px solid var(--line);border-radius:5px;padding:11px;background:var(--panel);color:var(--white);
  font-family:'Anton';font-size:16px;letter-spacing:.05em;text-transform:uppercase;cursor:pointer;transition:border-color .14s,color .14s}
.protocol .toBtn.armed{border-color:var(--yellow);color:var(--yellow)}
.protocol .toBtn.active{border-color:var(--red2);color:var(--red2);background:rgba(226,33,28,.08)}
.protocol .toBtn:disabled{opacity:.3;cursor:not-allowed}
.protocol /* flash */
.flash{position:fixed;inset:0;pointer-events:none;z-index:40;opacity:0}
.protocol .flash.go{animation:pr-fg .5s ease}
.protocol .flash.ng{animation:pr-fn .5s ease}
@keyframes pr-pr-fg{0%{opacity:.24;background:radial-gradient(circle,var(--green),transparent 68%)}100%{opacity:0}}
@keyframes pr-fn{0%{opacity:.24;background:radial-gradient(circle,var(--red2),transparent 68%)}100%{opacity:0}}
.protocol /* ---------- RESULTS ---------- */
.res{text-align:center;margin-top:20px}
.protocol .res .crown{font-family:'Oswald';font-weight:700;font-size:12px;letter-spacing:.2em;text-transform:uppercase;color:var(--dim)}
.protocol .res h2{font-family:'Anton';font-size:44px;text-transform:uppercase;line-height:.92;margin:7px 0 2px;
  -webkit-text-stroke:2px #000;paint-order:stroke fill}
.protocol .res h2.perfect{color:var(--green)}
.protocol .res h2.win{color:var(--yellow)}
.protocol .res h2.miss{color:var(--red2)}
.protocol .bigstat{font-family:'Anton';font-size:84px;line-height:.82;color:var(--white);margin:12px 0 0;font-variant-numeric:tabular-nums;
  -webkit-text-stroke:2px #000;paint-order:stroke fill}
.protocol .bigstat .u{font-size:28px;color:var(--dim);-webkit-text-stroke:0}
.protocol .statrow{display:flex;justify-content:center;gap:24px;margin:16px 0 4px}
.protocol .statrow .s .n{font-family:'Anton';font-size:28px;line-height:1;font-variant-numeric:tabular-nums}
.protocol .statrow .s .k{font-family:'Oswald';font-weight:500;font-size:9px;letter-spacing:.1em;text-transform:uppercase;color:var(--dim);margin-top:3px}
.protocol /* wordle grid on results */
.wordle{margin:18px auto 4px;display:inline-flex;flex-direction:column;gap:5px}
.protocol .wrow{display:flex;gap:5px;justify-content:center}
.protocol .wcell{font-size:19px;line-height:1;width:24px;text-align:center}
.protocol .sharecard{border:2px solid var(--line);border-radius:5px;padding:14px;margin:16px 0 4px;background:#000;
  font-family:'Barlow Condensed';font-weight:600;font-size:14px;line-height:1.5;color:var(--white);white-space:pre-line;text-align:left}
.protocol .footnote{font-family:'Barlow Condensed';font-size:11px;color:var(--dim);text-align:center;margin-top:20px;opacity:.55;line-height:1.5}
.protocol .hidden{display:none!important}`;

// Escape a team name for use inside the onerror-injected placeholder markup.
function safeName(name) {
  return (name || "").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

function fmtClock(ms) {
  const s = Math.ceil(ms / 1000);
  return Math.floor(s / 60) + ":" + String(s % 60).padStart(2, "0");
}

export default function Protocol({ onExit }) {
  // ----- screen: 'home' | 'setup' | 'play' | 'res'
  const [screen, setScreen] = useState("home");

  // ----- setup selections
  const [mode, setMode] = useState(null); // 'perfect' | 'two'
  const [cards, setCards] = useState(4); // 4 (Pro) | 6 (MVP)
  const [league, setLeague] = useState(null); // 'NFL'... | 'SINK'

  // ----- live round
  const [round, setRound] = useState(null); // {cards, league, solution}
  const [sel, setSel] = useState([]); // selected card indices
  const [marks, setMarks] = useState({}); // index -> 'sel1'|'sel2'|'correct'|'miss'|'dim'
  const [locked, setLocked] = useState(false);

  // ----- scoring / progress
  const [outs, setOuts] = useState(0);
  const [hits, setHits] = useState(0);
  const [record, setRecord] = useState([]); // bool[] per out (perfect mode)
  const [correct, setCorrect] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);

  // ----- clock
  const [clockText, setClockText] = useState("0.0");
  const [paused, setPaused] = useState(false);
  const [timeoutUsed, setTimeoutUsed] = useState(false);

  // ----- flash overlay: '' | 'go' | 'ng'
  const [flash, setFlash] = useState("");

  // ----- results
  const [res, setRes] = useState(null); // computed result payload
  const [shareText, setShareText] = useState("");
  const [shareLabel, setShareLabel] = useState("Share Result");

  // ----- refs that the RAF clock reads without re-subscribing
  const lockUntilRef = useRef(0);
  const clockRAF = useRef(0);
  const clockStart = useRef(0);
  const elapsedMs = useRef(0);
  const twoDeadline = useRef(0);
  const pauseStart = useRef(0);
  const pausedRef = useRef(false);
  const endedRef = useRef(false);
  // mirror scoring into refs so evaluate()/endGame() read fresh values inside timeouts
  const outsRef = useRef(0);
  const hitsRef = useRef(0);
  const recordRef = useRef([]);
  const correctRef = useRef(0);
  const attemptsRef = useRef(0);
  const bestStreakRef = useRef(0);
  const streakRef = useRef(0);

  useEffect(() => { pausedRef.current = paused; }, [paused]);

  // League team counts for the picker.
  const leagueCounts = {};
  LEAGUES.forEach((l) => { leagueCounts[l] = TEAMS.filter((t) => t.league === l).length; });

  // Scroll to top when the screen changes (matches original show()).
  useEffect(() => { window.scrollTo(0, 0); }, [screen]);

  // Tear down the clock on unmount so leaving the game never leaves a RAF running.
  useEffect(() => () => cancelAnimationFrame(clockRAF.current), []);

  // ===== NAV =====
  const chooseMode = (m) => {
    setMode(m);
    setLeague(null);
    setCards(4);
    setScreen("setup");
  };

  const backToHome = () => {
    cancelAnimationFrame(clockRAF.current);
    setScreen("home");
  };

  // ===== ROUND FLOW =====
  const nextRound = useCallback(() => {
    const lg = roundLeague(league);
    const r = buildRound(lg, cards);
    setRound(r);
    setSel([]);
    setMarks({});
    setLocked(false);
    lockUntilRef.current = performance.now() + INPUT_LOCK_MS;
  }, [league, cards]);

  // ===== CLOCK =====
  // Plain function (not memoized): it's only ever called synchronously from
  // startGame, never handed to a dependency array or effect, so it reads `mode`
  // and endGame straight from the current render — no stale-closure risk and no
  // dep array to keep honest.
  const startClock = () => {
    cancelAnimationFrame(clockRAF.current);
    const tick = () => {
      if (!pausedRef.current) {
        if (mode === "two") {
          const left = Math.max(0, twoDeadline.current - performance.now());
          setClockText(fmtClock(left));
          if (left <= 0) { endGame(); return; }
        } else {
          elapsedMs.current = performance.now() - clockStart.current;
          setClockText((elapsedMs.current / 1000).toFixed(1));
        }
      }
      clockRAF.current = requestAnimationFrame(tick);
    };
    tick();
  };

  // ===== START =====
  const startGame = () => {
    endedRef.current = false;
    // reset scoring
    setOuts(0); outsRef.current = 0;
    setHits(0); hitsRef.current = 0;
    setRecord([]); recordRef.current = [];
    setCorrect(0); correctRef.current = 0;
    setAttempts(0); attemptsRef.current = 0;
    setStreak(0); streakRef.current = 0;
    setBestStreak(0); bestStreakRef.current = 0;
    setPaused(false); pausedRef.current = false;
    setTimeoutUsed(false);
    setFlash("");
    setScreen("play");

    if (mode === "two") {
      twoDeadline.current = performance.now() + TWO_MIN_SECONDS * 1000;
      setClockText(fmtClock(TWO_MIN_SECONDS * 1000));
    } else {
      clockStart.current = performance.now();
      setClockText("0.0");
    }
    // build the first round and start the clock on the next frame
    const lg = roundLeague(league);
    const r = buildRound(lg, cards);
    setRound(r);
    setSel([]);
    setMarks({});
    setLocked(false);
    lockUntilRef.current = performance.now() + INPUT_LOCK_MS;
    startClock();
  };

  // ===== TAP =====
  const tapCard = (i) => {
    if (locked) return;
    if (performance.now() < lockUntilRef.current) return;
    if (sel.includes(i)) return;
    const nextSel = [...sel, i];
    setSel(nextSel);
    setMarks((m) => ({ ...m, [i]: "sel" + nextSel.length }));
    if (nextSel.length === 2) evaluate(nextSel);
  };

  // ===== EVALUATE =====
  const evaluate = (selPair) => {
    setLocked(true);
    const [i, j] = selPair;
    const rc = round.cards;
    const sol = round.solution;
    const picked = new Set([rc[i].team.id, rc[j].team.id]);
    const ok = picked.size === 2 && [...picked].every((id) => sol.has(id));

    attemptsRef.current += 1; setAttempts(attemptsRef.current);

    const nextMarks = {};
    rc.forEach((c, k) => {
      const isSol = sol.has(c.team.id);
      const isPicked = selPair.includes(k);
      if (isSol && isPicked) nextMarks[k] = "correct";
      else if (!isSol && isPicked) nextMarks[k] = "miss";
      else if (isSol && !isPicked) nextMarks[k] = "correct";
      else nextMarks[k] = "dim";
    });
    setMarks(nextMarks);

    doFlash(ok);

    if (ok) {
      correctRef.current += 1; setCorrect(correctRef.current);
      streakRef.current += 1; setStreak(streakRef.current);
      bestStreakRef.current = Math.max(bestStreakRef.current, streakRef.current);
      setBestStreak(bestStreakRef.current);
    } else {
      streakRef.current = 0; setStreak(0);
    }

    if (mode === "perfect") {
      recordRef.current = [...recordRef.current, ok]; setRecord(recordRef.current);
      outsRef.current += 1; setOuts(outsRef.current);
      if (!ok) { hitsRef.current += 1; setHits(hitsRef.current); }
      setTimeout(() => {
        if (outsRef.current >= PERFECT_OUTS) endGame();
        else nextRound();
      }, FEEDBACK_MS);
    } else {
      setTimeout(() => {
        if (performance.now() < twoDeadline.current) nextRound();
        else endGame();
      }, FEEDBACK_MS);
    }
  };

  const doFlash = (ok) => {
    setFlash("");
    // force a reflow-equivalent: set on next tick so the animation restarts
    requestAnimationFrame(() => setFlash(ok ? "go" : "ng"));
  };

  // ===== TIMEOUT (2 Minute Warning only) =====
  const callTimeout = () => {
    if (mode !== "two" || timeoutUsed) return;
    if (!pausedRef.current) {
      pausedRef.current = true; setPaused(true);
      pauseStart.current = performance.now();
      setTimeoutUsed(true);
    } else {
      twoDeadline.current += performance.now() - pauseStart.current;
      pausedRef.current = false; setPaused(false);
    }
  };

  // ===== END + RESULTS =====
  const endGame = () => {
    if (endedRef.current) return;
    endedRef.current = true;
    cancelAnimationFrame(clockRAF.current);
    if (mode === "perfect") renderPerfect();
    else renderTwo();
    setScreen("res");
  };

  const renderPerfect = () => {
    const h = hitsRef.current;
    const perfect = h === 0;
    const secs = elapsedMs.current / 1000;
    const rec = recordRef.current;
    // wordle grid 9 rows x 3
    const wgrid = [];
    for (let r = 0; r < 9; r++) {
      const row = [];
      for (let c = 0; c < 3; c++) {
        const idx = r * 3 + c;
        row.push(idx < rec.length ? (rec[idx] ? "\u26BE" : "\u274C") : "\u00B7");
      }
      wgrid.push(row);
    }
    setRes({
      kind: "perfect",
      perfect,
      crown: perfect ? "\u26BE Perfect Game" : "Final Line",
      titleClass: perfect ? "perfect" : "miss",
      title: perfect ? "PERFECTO" : h + " Hit" + (h > 1 ? "s" : "") + " Allowed",
      big: (PERFECT_OUTS - h) + "|/" + PERFECT_OUTS,
      stats: [
        [h, "Hits"],
        [secs.toFixed(1) + "s", "Time"],
        [bestStreakRef.current, "Best Run"],
      ],
      wgrid,
    });
    setShareText(buildPerfectShare(perfect, secs));
    setShareLabel("Share Result");
  };

  const renderTwo = () => {
    const acc = attemptsRef.current ? Math.round((100 * correctRef.current) / attemptsRef.current) : 0;
    setRes({
      kind: "two",
      crown: "\u23F1 Time Expired",
      titleClass: "win",
      title: correctRef.current + " Correct",
      big: "" + correctRef.current,
      stats: [
        [bestStreakRef.current, "Best Streak"],
        [attemptsRef.current, "Attempts"],
        [acc + "%", "Accuracy"],
      ],
      wgrid: null,
    });
    setShareText(buildTwoShare(acc));
    setShareLabel("Share Result");
  };

  const buildPerfectShare = (perfect, secs) => {
    const lg = league === "SINK" ? "Kitchen Sink" : league;
    const diff = cards === 6 ? " [MVP]" : "";
    const rec = recordRef.current;
    let grid = "";
    for (let r = 0; r < 9; r++) {
      let line = "";
      for (let c = 0; c < 3; c++) {
        const idx = r * 3 + c;
        line += idx < rec.length ? (rec[idx] ? "\u26BE" : "\u274C") : "\u2B1B";
      }
      grid += line + "\n";
    }
    const h = hitsRef.current;
    return (
      "THE PROTOCOL \u2014 Perfect Game (" + lg + ")" + diff + "\n" +
      (perfect
        ? "\u26BE PERFECTO \u2014 27 up, 27 down"
        : (PERFECT_OUTS - h) + "/27 retired \u00B7 " + h + " hit" + (h > 1 ? "s" : "")) +
      " \u00B7 " + secs.toFixed(1) + "s\n" + grid
    );
  };

  const buildTwoShare = (acc) => {
    const lg = league === "SINK" ? "Kitchen Sink" : league;
    const diff = cards === 6 ? " [MVP]" : "";
    return (
      "THE PROTOCOL \u2014 2 Minute Warning (" + lg + ")" + diff + "\n" +
      correctRef.current + " correct in 2:00 \u00B7 best streak " + bestStreakRef.current + " \u00B7 " + acc + "% acc"
    );
  };

  const share = async () => {
    const text = (shareText || "") + "\n\nPlay: " + window.location.href;
    try {
      if (navigator.share) { await navigator.share({ text }); return; }
    } catch (e) { /* fall through to clipboard */ }
    try {
      await navigator.clipboard.writeText(text);
      setShareLabel("Copied \u2713");
      setTimeout(() => setShareLabel("Share Result"), 1600);
    } catch (e) {
      setShareLabel("Select & copy below");
    }
  };

  const playAgain = () => {
    // back to setup for the same mode, mirroring a fresh run without a full reload
    endedRef.current = false;
    setScreen("home");
  };

  // ===== RENDER HELPERS =====
  const cardClass = (i) => {
    let cls = "card";
    const m = marks[i];
    if (m === "sel1" || m === "sel2") cls += " sel";
    else if (m === "correct") cls += " correct";
    else if (m === "miss") cls += " miss";
    else if (m === "dim") cls += " dim";
    return cls;
  };
  const markGlyph = (i) => {
    const m = marks[i];
    if (m === "sel1") return "1";
    if (m === "sel2") return "2";
    if (m === "correct") return "\u2713";
    if (m === "miss") return "\u2717";
    return "";
  };

  const hudLeague = round
    ? (league === "SINK"
        ? <span><b>{round.league}</b> · sink</span>
        : round.league)
    : (league === "SINK" ? <span><b>KITCHEN</b> SINK</span> : league);

  return (
    <div className="protocol">
      <style>{styles}</style>
      <div className={"flash" + (flash ? " " + flash : "")} />
      <div id="app">

        {/* ---------- HOME ---------- */}
        <section className={"screen" + (screen === "home" ? " on" : "")}>
          <img
            className="title-art"
            src={ASSET_DIR + "title_image.png"}
            alt="The Protocol"
            onError={(e) => {
              e.currentTarget.outerHTML =
                '<div style="font-family:Anton;font-size:52px;text-align:center;color:#fff;margin:20px 0" class="outline">THE PROTOCOL</div>';
            }}
          />
          <div className="subtitle">Logo × Name Interference</div>
          <div className="rulebox">
            <p>Four logos, four names — <b>all four are wrong</b>. Two had their names <b>swapped with each other</b>. Tap those two to swap them back.</p>
            <div className="ex">Cowboys logo reads <span className="sw">"FALCONS"</span> · Falcons logo reads <span className="sw">"COWBOYS"</span> — that's the pair.</div>
          </div>
          <div className="pick-label">Choose your game</div>
          <div className="modes">
            <button className="mode perfect" onClick={() => chooseMode("perfect")}>
              <div className="art"><img src={ASSET_DIR + "perfect.png"} alt="Perfect Game" onError={(e) => { e.currentTarget.style.display = "none"; }} /></div>
              <div className="cap">
                <div className="nm">Perfect<br />Game</div>
                <span className="tag">27 Outs</span>
                <div className="md">Retire 27 straight. A miss is a baserunner — chase the perfecto.</div>
              </div>
            </button>
            <button className="mode two" onClick={() => chooseMode("two")}>
              <div className="art"><img src={ASSET_DIR + "2min.png"} alt="2 Minute Warning" onError={(e) => { e.currentTarget.style.display = "none"; }} /></div>
              <div className="cap">
                <div className="nm">2 Minute<br />Warning</div>
                <span className="tag">2:00 Clock</span>
                <div className="md">Score as many as you can before time runs out.</div>
              </div>
            </button>
          </div>
          <button className="btn ghost" onClick={onExit} style={{ marginTop: 18 }}>Exit</button>
        </section>

        {/* ---------- SETUP ---------- */}
        <section className={"screen" + (screen === "setup" ? " on" : "")}>
          <div className="pick-label">{mode === "perfect" ? "Perfect Game" : "2 Minute Warning"}</div>
          <div className="diff">
            <button className={"d" + (cards === 4 ? " sel" : "")} onClick={() => setCards(4)}>
              <div className="dt">PRO</div><div className="dd">4 cards</div>
            </button>
            <button className={"d" + (cards === 6 ? " sel" : "")} onClick={() => setCards(6)}>
              <div className="dt">MVP</div><div className="dd">6 cards · harder</div>
            </button>
          </div>
          <div className="pick-label" style={{ marginTop: 20 }}>League</div>
          <div className="leagues">
            {LEAGUES.map((l) => (
              <button key={l} className={"lg" + (league === l ? " sel" : "")} onClick={() => setLeague(l)}>
                <div className="logo-chip">
                  <img
                    src={ASSET_DIR + l.toLowerCase() + ".png"}
                    alt={l}
                    onError={(e) => { e.currentTarget.parentNode.outerHTML = '<div class="ab">' + l + "</div>"; }}
                  />
                </div>
                <div className="ct">{leagueCounts[l]} teams</div>
              </button>
            ))}
            <button className={"lg sink" + (league === "SINK" ? " sel" : "")} onClick={() => setLeague("SINK")}>
              <div className="logo-chip">
                {LEAGUES.map((l) => (
                  <img key={l} src={ASSET_DIR + l.toLowerCase() + ".png"} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                ))}
              </div>
              <div className="ct">Kitchen Sink · all leagues</div>
            </button>
          </div>
          <button className="btn" disabled={!league} onClick={startGame}>
            {mode === "perfect" ? "Play Ball" : "Start Clock"}
          </button>
          <button className="btn ghost" onClick={backToHome}>Back</button>
        </section>

        {/* ---------- PLAY ---------- */}
        <section className={"screen" + (screen === "play" ? " on" : "")}>
          <div className="hud">
            <div className="chip">{hudLeague}</div>
            <div className={"clock" + (mode === "two" ? " count" : "") + (paused ? " paused" : "")}>{clockText}</div>
            <div className="prog">
              {mode === "perfect"
                ? <span>OUT <b>{Math.min(outs + 1, PERFECT_OUTS)}</b>/{PERFECT_OUTS}</span>
                : <span><b>{correct}</b> in</span>}
            </div>
          </div>

          <div className="strip">
            {mode === "perfect" ? (
              <>
                <span className="lbl">Outs</span>
                {Array.from({ length: PERFECT_OUTS }).map((_, i) => (
                  <div key={i} className={"pip" + (i < record.length ? (record[i] ? " good" : " bad") : "")} />
                ))}
              </>
            ) : (
              <span className="lbl">
                {correct + " correct · streak " + streak + (bestStreak > 1 ? " · best " + bestStreak : "")}
              </span>
            )}
          </div>

          <div className="prompt">Tap the <b>two swapped</b> cards</div>

          <div className={"grid " + (cards === 6 ? "g6" : "g4") + (locked ? " locked" : "")}>
            {round && round.cards.map((c, i) => (
              <div key={i} className={cardClass(i)} data-i={i} onClick={() => tapCard(i)}>
                <div className="ord">{String.fromCharCode(65 + i)}</div>
                <div className="mk" style={{ display: markGlyph(i) ? "flex" : "none" }}>{markGlyph(i)}</div>
                <div className="lw">
                  <img
                    src={ASSET_DIR + c.team.logo}
                    alt=""
                    onError={(e) => {
                      e.currentTarget.parentNode.innerHTML =
                        '<div class="noimg">' + safeName(c.team.name) + "</div>";
                    }}
                  />
                </div>
                <div className="label">{c.text}</div>
              </div>
            ))}
          </div>

          {mode === "two" && (
            <div className="toBar">
              <button
                className={"toBtn " + (paused ? "active" : "armed")}
                onClick={callTimeout}
                disabled={timeoutUsed && !paused}
              >
                {timeoutUsed ? (paused ? "Resume Clock" : "Timeout Used") : "Call Timeout (1)"}
              </button>
            </div>
          )}
        </section>

        {/* ---------- RESULTS ---------- */}
        <section className={"screen" + (screen === "res" ? " on" : "")}>
          {res && (
            <>
              <div className="res">
                <div className="crown">{res.crown}</div>
                <h2 className={res.titleClass}>{res.title}</h2>
                <div className="bigstat">
                  {res.big.includes("|")
                    ? <>{res.big.split("|")[0]}<span className="u">{res.big.split("|")[1]}</span></>
                    : res.big}
                </div>
                <div className="statrow">
                  {res.stats.map(([n, k], idx) => (
                    <div key={idx} className="s"><div className="n">{n}</div><div className="k">{k}</div></div>
                  ))}
                </div>
                {res.wgrid && (
                  <div className="wordle">
                    {res.wgrid.map((row, r) => (
                      <div key={r} className="wrow">
                        {row.map((cell, c) => <div key={c} className="wcell">{cell}</div>)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <button className="btn" onClick={share}>{shareLabel}</button>
              <button className="btn ghost" onClick={playAgain}>Play Again</button>
              <div className="footnote">Team logos are property of their respective teams &amp; leagues.</div>
            </>
          )}
        </section>

      </div>
    </div>
  );
}
